import React from "react";

/**
 * Rainbow speedometer gauge styled after the Grindrod Equipment Performance Dashboard
 * reference: red 0-25, orange 25-50, yellow 50-75, green 75-100 (with optional purple band > target).
 *
 * Props:
 *  - value: numeric value (0..max)
 *  - max: scale max (100 for %, 96 for MTBF hours)
 *  - target: where the "above target" line sits (e.g. 90 for availability, 48 for MTBF)
 *  - unit: "%" or "HOURS"
 *  - label: gauge title (AVAILABILITY etc)
 *  - color: text color for the big value (cyan, amber, green, purple)
 *  - ticks: array of tick numbers, defaults [0, 25, 50, 75, 100] (or [0, 24, 48, 72, 96] for MTBF)
 *  - aboveTarget: bool — if true, render green "Above Target", else red "Below Target"
 *  - bands: optional custom color bands [{from,to,color}] otherwise rainbow default
 */
const RAINBOW_BANDS_PCT = [
    { from: 0, to: 25, color: "#D6202F" },
    { from: 25, to: 50, color: "#F08C1F" },
    { from: 50, to: 75, color: "#F0C81F" },
    { from: 75, to: 100, color: "#00B36B" },
];

const MTBF_BANDS = [
    { from: 0, to: 24, color: "#D6202F" },
    { from: 24, to: 48, color: "#F0C81F" },
    { from: 48, to: 72, color: "#00B36B" },
    { from: 72, to: 96, color: "#8B5CF6" },
];

export default function EquipmentGauge({
    value,
    max = 100,
    target = null,
    unit = "%",
    label = "",
    color = "#22D3EE",
    ticks = null,
    aboveTarget = null,
    bands = null,
    targetLabel = null,
}) {
    const size = 240;
    const stroke = 26;
    const cx = size / 2;
    const cy = size / 2 + 12;
    const radius = size / 2 - stroke / 2 - 6;
    const startAngle = 135; // bottom-left
    const endAngle = 45;    // bottom-right (sweeping clockwise across the top)
    const total = 270;      // arc span

    const safeValue = value == null || isNaN(value) ? 0 : Math.max(0, Math.min(max, value));
    const valuePct = safeValue / max;

    const usedBands = bands || (unit === "HOURS" ? MTBF_BANDS : RAINBOW_BANDS_PCT);
    const usedTicks = ticks || (unit === "HOURS" ? [0, 24, 48, 72, 96] : [0, 25, 50, 75, 100]);

    // helpers
    const polar = (angleDeg, r = radius) => {
        const a = (angleDeg - 90) * (Math.PI / 180);
        return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
    };
    const arcAngle = (v) => startAngle + (v / max) * total;

    // arc path between two values on [0, max]
    const arc = (from, to) => {
        const a1 = arcAngle(from);
        const a2 = arcAngle(to);
        const large = a2 - a1 > 180 ? 1 : 0;
        const p1 = polar(a1);
        const p2 = polar(a2);
        return `M ${p1.x} ${p1.y} A ${radius} ${radius} 0 ${large} 1 ${p2.x} ${p2.y}`;
    };

    // pointer
    const pointerAngle = arcAngle(safeValue);
    const tipBase = polar(pointerAngle, radius - 4);
    const baseR = 12;
    const leftBase = polar(pointerAngle + 90, baseR + 6);
    const rightBase = polar(pointerAngle - 90, baseR + 6);
    leftBase.x = cx + Math.cos((pointerAngle + 90 - 90) * Math.PI / 180) * 10;
    leftBase.y = cy + Math.sin((pointerAngle + 90 - 90) * Math.PI / 180) * 10;
    rightBase.x = cx + Math.cos((pointerAngle - 90 - 90) * Math.PI / 180) * 10;
    rightBase.y = cy + Math.sin((pointerAngle - 90 - 90) * Math.PI / 180) * 10;

    const targetMarker = target != null
        ? (() => {
            const a = arcAngle(target);
            const outer = polar(a, radius + stroke / 2 + 2);
            const t1 = polar(a, radius + stroke / 2 + 12);
            const t2 = polar(a + 2, radius + stroke / 2 + 4);
            const t3 = polar(a - 2, radius + stroke / 2 + 4);
            return { outer, points: `${t1.x},${t1.y} ${t2.x},${t2.y} ${t3.x},${t3.y}` };
        })()
        : null;

    return (
        <div className="rounded-lg p-4 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #0F1B3D 0%, #050A1F 100%)", boxShadow: "0 0 0 1px rgba(34,211,238,0.15), 0 8px 24px rgba(0,0,0,0.4)" }} data-testid={`gauge-${label.toLowerCase()}`}>
            <div className="text-center font-display font-black tracking-[0.18em] text-xl" style={{ color }}>{label}</div>
            {target != null && (
                <div className="text-center text-[11px] tracking-wider text-slate-300 mt-0.5">Target: ≥ {target}{unit === "HOURS" ? " hrs" : "%"}</div>
            )}
            <svg viewBox={`0 0 ${size} ${size + 30}`} className="w-full" style={{ maxWidth: 280 }}>
                {/* outer ring */}
                <circle cx={cx} cy={cy} r={radius + stroke / 2 + 6} fill="none" stroke="#22D3EE" strokeOpacity="0.12" strokeWidth="2" />
                {/* dark inner */}
                <circle cx={cx} cy={cy} r={radius - stroke / 2 - 2} fill="#02061A" />
                {/* color bands */}
                {usedBands.map((b, i) => (
                    <path key={i} d={arc(b.from, b.to)} stroke={b.color} strokeWidth={stroke} fill="none" strokeLinecap="butt" opacity="0.95" />
                ))}
                {/* tick marks (text labels) */}
                {usedTicks.map((t, i) => {
                    const p = polar(arcAngle(t), radius - stroke / 2 - 14);
                    return (
                        <text key={i} x={p.x} y={p.y} fontSize="13" fontWeight="700" fill="#ffffff" textAnchor="middle" dominantBaseline="central">{t}</text>
                    );
                })}
                {/* target marker triangle outside */}
                {targetMarker && (
                    <polygon points={targetMarker.points} fill={color} />
                )}
                {/* pointer */}
                <polygon
                    points={`${tipBase.x},${tipBase.y} ${leftBase.x},${leftBase.y} ${rightBase.x},${rightBase.y}`}
                    fill={color}
                    stroke={color}
                    strokeLinejoin="round"
                />
                <circle cx={cx} cy={cy} r={9} fill="#0B0B1F" stroke={color} strokeWidth="2" />
                <circle cx={cx} cy={cy} r={3} fill={color} />
                {/* value text */}
                <text x={cx} y={cy + radius / 2 + 6} textAnchor="middle" fontSize="36" fontWeight="900" fill={color}>
                    {value == null || isNaN(value) ? "—" : (unit === "HOURS" ? Number(value).toFixed(1) : Number(value).toFixed(1) + "%")}
                </text>
                {unit === "HOURS" && (
                    <text x={cx} y={cy + radius / 2 + 24} textAnchor="middle" fontSize="11" fontWeight="700" fill={color} opacity="0.85">HOURS</text>
                )}
            </svg>
            {/* status pill */}
            {aboveTarget != null && value != null && (
                <div className="text-center mt-1">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${aboveTarget ? "text-green-400" : "text-red-400"}`}>
                        <span className={`w-3 h-3 rounded-full ${aboveTarget ? "bg-green-400/20 border border-green-400" : "bg-red-400/20 border border-red-400"} flex items-center justify-center text-[8px]`}>{aboveTarget ? "✓" : "!"}</span>
                        {aboveTarget ? "Above Target" : "Below Target"}
                    </span>
                </div>
            )}
            {targetLabel && (
                <div className="text-center text-[10px] mt-1 text-slate-400">{targetLabel}</div>
            )}
        </div>
    );
}
